import { useMemo } from "react";

import dayjs from "@calcom/dayjs";

import type { Slots } from "../lib/types";
import { useNonEmptyScheduleDays } from "./useNonEmptyScheduleDays";

/**
 * True when the waitlist CTA should be offered: the acte opted into the waitlist (`thresholdDays` set)
 * and the currently-loaded schedule has NO bookable day within the next `thresholdDays` days.
 *
 * Returns false when the threshold is unset/non-positive, the slots haven't loaded yet (so the CTA never
 * flashes during loading), or any day inside the window has availability — keeping the public flow
 * strictly unchanged for actes that didn't opt in. Detection is bounded by the slots the booker already
 * fetched (its current view); the server re-derives the desired window on registration.
 */
export const useNoAvailabilityWithinThreshold = (
  slots: Slots | undefined,
  thresholdDays: number | null | undefined
): boolean => {
  const nonEmptyDays = useNonEmptyScheduleDays(slots);

  return useMemo(() => {
    if (!thresholdDays || thresholdDays <= 0) return false;
    if (typeof slots === "undefined") return false;
    const horizon = dayjs().add(thresholdDays, "day").endOf("day");
    const hasSlotWithinWindow = nonEmptyDays.some((date) => dayjs(date).isBefore(horizon));
    return !hasSlotWithinWindow;
  }, [slots, nonEmptyDays, thresholdDays]);
};
